<template>
  <div
    :class="[
      'form-lifestory',
      { '-is-submitting': isSubmitting },
    ]">
    <transition name="dropdown-fade">
      <form
        v-if="!updated"
        :disabled="isSubmitting"
        novalidate
        @submit="onSubmit">
        <input-text
          id="firstname"
          type="text"
          name="firstname"
          required
          :disabled="isSubmitting"
          autocomplete="given name"
          :label="$t('forms.labels.firstname')"
          @focus="showError = false" />

        <input-text
          id="email"
          type="email"
          name="email"
          required
          :disabled="isSubmitting"
          autocomplete="home email"
          :label="$t('forms.labels.email')" />

        <input-select-native
          id="kitchens"
          :placeholder="`${$t('forms.placeholder.selectKitchen')}`"
          name="kitchens"
          required
          :disabled="isSubmitting"
          autocomplete="cucine"
          :options="kitchens"
          :label="$t('forms.labels.kitchens')" />

        <input-select-native
          id="nation"
          v-model="nation"
          name="nation"
          :disabled="isSubmitting"
          :options="countries"
          sync
          required
          :placeholder="$t('storeLocator.page.form.nationPlaceholder')"
          :label="$t('forms.placeholder.purchaseCountry')" />

        <input-select-native
          v-if="nation == '00000'"
          id="province"
          v-model="province"
          autocomplete="provos"
          name="province"
          :disabled="isSubmitting"
          :options="provinces"
          sync
          outer-label
          :placeholder="$t('storeLocator.page.form.provincePlaceholder')"
          :label="$t('forms.warrantyOkForm.labels.provinciaRivenditore')" />

        <!-- STOREDS BASED ON COUNTRY SELECTION -->
        <input-select-native
          id="store"
          v-model="store"
          :class="[{ 'col-span-2': nation !== '00000' }]"
          :placeholder="`${$t('forms.placeholder.selectStore')}`"
          name="store"
          required
          outer-label
          sync
          :disabled="!nation || (nation == '00000' && !province) || !exists(filteredStores)"
          autocomplete="cucine"
          :options="filteredStores"
          :label="$t('forms.labels.stores')" />

        <!-- ALL STORES -->
        <!-- <input-select-native
          id="stores"
          :placeholder="`${$t('forms.placeholder.selectStore')}`"
          name="stores"
          required
          :disabled="isSubmitting"
          autocomplete="cucine"
          :options="stores"
          :label="$t('forms.labels.stores')" /> -->

        <input-text
          id="city"
          type="city"
          name="city"
          required
          :disabled="isSubmitting"
          :label="$t('forms.labels.city')" />

        <input-text
          id="surface"
          type="number"
          name="surface"
          required
          :disabled="isSubmitting"
          :label="$t('forms.labels.surface')" />

        <input-textarea
          id="message"
          class="col-span-2"
          name="message"
          :disabled="isSubmitting"
          :label="$t('forms.labels.message')" />

        <input-file
          id="images"
          class="col-span-2"
          :multiple="10"
          :formats="['image/jpeg']"
          name="images"
          required
          :disabled="isSubmitting"
          :max-file-size="0"
          :max-total-size="0"
          :sublabel="$t('forms.labels.images_horizontal')"
          :label="$t('forms.labels.file')" />

        <base-button
          class="form-lifestory__policy-btn"
          type="button"
          :label="$t('forms.lifeStories.readPolicy')"
          mode="tertiary"
          :href="$t('forms.lifeStories.policyUrl')">
          {{ $t('forms.lifeStories.readPolicy') }}
        </base-button>

        <div class="col-span-2 -compliance">
          <input-checkbox
            id="marketing"
            name="marketing"
            required
            :label="$t('forms.lifeStories.complianceMarketing')" />
          <input-checkbox
            id="profiling"
            name="profiling"
            required
            :label="$t('forms.lifeStories.complianceProfiling')" />
          <input-checkbox
            id="marketing2"
            name="marketing2"
            required
            :label="$t('forms.lifeStories.complianceMarketing2')" />
        </div>

        <div class="form-lifestory__advice col-span-2">
          {{ $t('forms.lifeStories.advice') }}
        </div>

        <cta
          type="submit"
          class="form-lifestory__cta col-span-2"
          :text="$t('forms.labels.confirm')"
          variant="solid"
          color="red"
          :disabled="isSubmitting" />
      </form>

      <div
        v-else
        class="form-lifestory__complete">
        <h3>
          {{ $t('forms.success.title') }}
        </h3>
        <div v-html="$t('forms.success.message')" />
      </div>
    </transition>

    <loading-indicator
      v-show="isSubmitting"
      infinite />
  </div>
</template>

<script setup>
  import * as yup from 'yup'

  import { getStoresByForeignCountry } from '~/graphql/stores/deta-fetch'
  import { COUNTRIES, PROVINCES } from '~/graphql/stores/store-locator'
  import { useLifeStoriesStore } from '~/store/life-stories'
  import { getStoreDisplayTitleWithLocation } from '~/utils/stores'

  const { t, locale } = useI18n()
  const italyEntryId = useRuntimeConfig().public.ITALY_ENTRY_ID

  const props = defineProps({
    title: String,
    kitchens: Array,
    stores: Array
  })

  const emit = defineEmits([ 'submit:successful' ])

  const showError = ref(false)
  const updated = ref(false)
  const lifestories = useLifeStoriesStore()

  const store = ref(null)
  const nation = ref(null)
  const province = ref(null)
  const provinces = ref([ ])
  const foreignStores = ref([])

  const validFileExtensions = {
    image: [ 'jpg', 'jpeg' ]
  }

  const isValidFileType = (files, fileType) => {
    let validate
    for (let index = 0; index < files.length; index++) {
      const element = files[index]?.filename
      validate = element && validFileExtensions[fileType].indexOf(element.split('.').pop()) > -1
    }
    return validate
  }

  const {
    handleSubmit,
    isSubmitting,
    resetForm
  } = useForm({
    validationSchema: yup.object({
      'firstname': yup.string().required(),
      'email': yup.string().email().required(),
      'kitchens': yup.string().required(),
      'store': yup.string().required(),
      'city': yup.string().required(),
      'surface': yup.string().required(),
      'images': yup
        .mixed()
        .required()
        .test('is-valid-type', t('forms.errors.file_type'),
              value => isValidFileType(value, 'image'))
        .test('max-length', t('forms.errors.files_number'),
              value => {
                if (value.length > 8) {
                  return false
                } else {
                  return true
                }
              }),
      'message': yup.string(),
      'marketing': yup
        .boolean()
        .oneOf([ true ], 'Must Accept Terms and Conditions').required(),
      'profiling': yup
        .boolean()
        .oneOf([ true ], 'Must Accept Terms and Conditions').required(),
      'marketing2': yup
        .boolean()
        .oneOf([ true ], 'Must Accept Terms and Conditions').required()
    })
  })

  const onSubmit = handleSubmit(async values => {
    const kitchenName = props.kitchens?.find(k => k.id?.toString() === values.kitchens?.toString())?.title
    const storeName = filteredStores.value?.find(k => k.id?.toString() === values.store?.toString())?.title

    await lifestories.submitStory({
      ...values, kitchenName, storeName
    })
    updated.value = true
    setTimeout(() => {
      resetForm()
      emit('submit:successful')
    }, 5000)
  })

  const { data: countriesData, error: countriesError } = await useAsyncData(
    `storelocator-countries-${locale.value}`,
    () => useApi(COUNTRIES, { countriesLabel: true }),
    {
      transform: r => r?.types || []
    }
  )

  if (countriesError.value) {
    console.error('Error fetching store locator countries: ', countriesError.value)
  }

  const getStoresByCountry = async () => {
    const results = await getStoresByForeignCountry(nation.value)

    foreignStores.value = results.map(r => ({
      ...r,
      label: getStoreDisplayTitleWithLocation(r),
      value: r.id
    }))
  }

  const getProvinces = async () => {
    const { items } = await useApi(PROVINCES)

    provinces.value = items.map(p => ({
      label: p.title,
      value: p.slug
    }))
  }

  const countries = computed(() => {
    const otherCountries = (countriesData.value ?? [])
      .map(d => ({ label: d.title, value: d.id }))
      // Italy is prepended as the fixed '00000' option: the Craft Italy entry must be filtered
      // out or it would appear twice and dead-end the store selection (no province cascade).
      .filter(d => d.label && String(d.value) !== String(italyEntryId))
      .sort((a, b) => a.label.localeCompare(b.label))

    return [ { label: t('storeLocator.page.titleItaly'), value: '00000' }, ...otherCountries ]
  })

  const filteredStores = computed(() => {
    if (nation.value == '00000') {
      return props.stores
        .filter(s => getFirst(s.province)?.slug === province.value)
        .map(store => ({
          ...store,
          label: getStoreDisplayTitleWithLocation(store)
        }))
    }

    // abroad
    return [ ...foreignStores.value ]
  })

  watch(() => nation.value, async (nval, oval) => {
    await nextTick()

    province.value = null
    store.value = null
    foreignStores.value = []

    if (nval != oval) {
      if (nval != '00000') {
        getStoresByCountry()
      } else {
        getProvinces()
      }
    }
  }, { immediate: true })
</script>

<style lang="scss">
  .form-lifestory {
    padding-top: rem(40px);
    padding-bottom: rem(40px);
    display: block;
    margin: 0 auto;
    max-width: rem(1024px);

    @include mq(medium) {
      @include wrapper;
    }

    &.-small {
      .form-lifestory__title {
        font-size: rem(24px);
        margin-bottom: rem(40px);
      }

      @include mq(medium) {
        max-width: rem(660px);
      }
    }

    &.-is-submitting {
      form {
        pointer-events: none;
        opacity: 0.5;
      }
    }

    &__title {
      text-align: center;
      margin-bottom: rem(120px);
    }

    >form {
      display: flex;
      flex-direction: column;
      gap: 40px;

      @include mq(medium) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: rem(40px) rem(16px);
      }

      >.btn[type="submit"] {
        align-self: center;
        margin: auto;
        grid-column: span 2;
        margin-top: rem(20px);
      }
    }

    >form > div {
      grid-column: auto / span 1;
    }

    .col-span-2 {
      grid-column: 1 / span 2;
    }

    .loading-indicator {
      position: fixed;
      left: calc(50% - 40px);
      top: calc(50% - 2.5px);
    }

    &__complete {
      text-align: center;

      h3 {
        font-size: 42px;
      }
    }

    .-compliance {
      display: flex;
      flex-direction: column;
      gap: calc(var(--grid-gutter) * 3);

      @include mq(medium) {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: calc(var(--grid-gutter) * 2);
      }

      .input-checkbox:last-child {
        grid-column: 1 / span 2;
      }
    }

    &__cta {
      margin: auto;
    }

    &__advice {
      font-size: rem(13px);
    }

    &__policy-btn {
      grid-column: 1 / span 1;
      margin: 0 auto 0 0;
      text-decoration: underline;
    }
  }
</style>
